import { useLocalization } from "@fluent/react";
import { useBreakpoints } from "@narmi/design_system";
import { Card } from "@lending/components/Card/Card";
import { InfoRow } from "@lending/components/InfoRow/InfoRow";
import { PlainButton } from "@lending/components/PlainButton/PlainButton";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { ApplicationApplicantDetailsRead } from "@lending/types";
import { formatAddress, formatDate, formatPhoneNumber } from "@lending/utils/formatters";
import styles from "../../ConfirmInfo.module.css";

// Shown when we know a value is on file (has_tax_id / has_date_of_birth) but the /sensitive endpoint
// is not available to this session, so we can't display the real (already masked) value.
const MASKED_SSN_PLACEHOLDER = "•••-••-••••";
const MASKED_DOB_PLACEHOLDER = "••/••/••••";

interface ApplicantConfirmCardProps {
  applicant: ApplicationApplicantDetailsRead;
  askMailingAddress: boolean;
  onClickEdit: (applicant: ApplicationApplicantDetailsRead) => void;
}

/**
 * A single applicant's confirmation card. SSN and date of birth are sensitive and no longer returned
 * on the applicant read, so each card fetches them from the per-applicant /sensitive endpoint (the
 * SSN comes back already masked). When /sensitive is unavailable, the has_* flags let us still show a
 * masked placeholder so the applicant knows the value is on file.
 */
function ApplicantConfirmCard({
  applicant,
  askMailingAddress,
  onClickEdit,
}: ApplicantConfirmCardProps) {
  const { l10n } = useLocalization();
  const { m, largestSatisfiedBreakpoint } = useBreakpoints();
  const { data: sensitive } = useGetApplicationApplicantDetailsSensitiveQuery(applicant.id);

  const taxId = sensitive?.tax_id ?? (applicant.has_tax_id ? MASKED_SSN_PLACEHOLDER : "");
  const dateOfBirth = sensitive?.date_of_birth
    ? formatDate(sensitive.date_of_birth)
    : applicant.has_date_of_birth
      ? MASKED_DOB_PLACEHOLDER
      : "";

  const residentialAddress = applicant.residential_address;
  const mailingAddress = applicant.mailing_address;

  return (
    <div className={styles.applicantCardContainer}>
      {largestSatisfiedBreakpoint === "s" && (
        <div className={styles.mobileEditButtonContainer}>
          <PlainButton
            label={l10n.getString("confirm-edit-button", null, "Edit")}
            onClick={() => onClickEdit(applicant)}
          />
        </div>
      )}
      <Card>
        <div className={styles.applicantInfo}>
          {m && (
            <div className={styles.edit}>
              <PlainButton
                label={l10n.getString("confirm-edit-button", null, "Edit")}
                onClick={() => onClickEdit(applicant)}
              />
            </div>
          )}
          <InfoRow
            label={l10n.getString("confirm-name-label", null, "Name")}
            value={`${applicant.first_name} ${applicant.last_name}`}
          />
          <InfoRow label={l10n.getString("confirm-ssn-label", null, "SSN")} value={taxId} />
          <InfoRow
            label={l10n.getString("confirm-dob-label", null, "Date of birth")}
            value={dateOfBirth}
          />
          <InfoRow
            label={l10n.getString("confirm-email-label", null, "Email")}
            value={applicant.email}
          />
          <InfoRow
            label={l10n.getString("confirm-phone-label", null, "Phone")}
            value={formatPhoneNumber(applicant.phone)}
          />
          <InfoRow
            label={l10n.getString("confirm-residential-address-label", null, "Residential address")}
            value={residentialAddress ? formatAddress(residentialAddress) : ""}
          />
          {askMailingAddress && (
            <InfoRow
              label={l10n.getString("confirm-mailing-address-label", null, "Mailing address")}
              value={
                mailingAddress
                  ? formatAddress(mailingAddress)
                  : l10n.getString(
                      "confirm-mailing-address-same-as-residential",
                      null,
                      "Same as residential address"
                    )
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export { ApplicantConfirmCard };
